import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from 'contexts/auth_context/AuthContext';
import { themeContext } from 'contexts/theme_context/ThemeContext';
import { languageContext } from 'contexts/language_context/LanguageContext';
import LoadingScreen from 'components/loading_screen/LoadingScreen';
import {
    CLIENT_OPERATING_ANALYSIS_TYPES,
    getCachedClientOperatingAnalysis,
    getClientOperatingAnalysisRefreshStatus,
    getAnalysisLabel,
    isRefreshActive,
    requestClientOperatingAnalysisRefresh,
} from 'services/clientOperatingAnalysisService';
import {
    PRODUCT_ANALYTICS_EVENTS,
    trackProductAnalyticsEvent,
} from 'services/productAnalyticsService';
import {
    COLUMN_WIDTH_LIMITS,
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
    SORT_DIRECTIONS,
    clearStoredColumnWidths,
    clearStoredVisibleColumns,
    clampColumnWidth,
    doesRowMatchSearchQuery,
    doesRowMatchVehicleIdentity,
    formatCellValue,
    formatColumnLabel,
    getAnalysisSummaryStats,
    getColumnAlignmentClass,
    getAnalysisRowKey,
    getColumnLayoutClass,
    getColumnPreferredWidth,
    getDefaultVisibleColumns,
    getOperatingImpactSummary,
    getPaginationRange,
    getPaginationSummary,
    paginateRows,
    readStoredColumnWidths,
    readStoredVisibleColumns,
    resolveColumnWidths,
    resolveVisibleColumns,
    sortRowsByColumn,
    writeStoredColumnWidths,
    writeStoredVisibleColumns,
} from './clientOperatingAnalysisTable';
import { buildOperatingAnalysisXlsxBlob, downloadBlob, getSafeXlsxFilename } from '../../utils/xlsxExport';
import VehicleModelFilter from './VehicleModelFilter';
import './ClientOperatingAnalysis.css';

const MAX_RANGE_DAYS = 366;
const POLL_INTERVAL_MS = 3000;
const DEFAULT_REFRESH_TIMEOUT_SECONDS = 15 * 60;
const REFRESH_TIMEOUT_BUFFER_MS = 30000;
const DATE_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MOBILE_OPERATING_ANALYSIS_QUERY = '(max-width: 768px)';
const INTERNAL_SOURCE_PREFIX_PATTERN = new RegExp(['Athe', 'na\\b[:\\s-]*'].join(''), 'gi');

const getCurrentAnalyticsPage = () => (
    typeof window !== 'undefined' ? window.location.pathname : '/customer-portal/operating-analysis'
);

const DEFAULT_OPERATING_ANALYSIS_COPY = {
    title: 'Operating Analysis',
    action: 'Operating Analysis',
    dateRangeAction: 'Date range',
    noRangeSelected: 'No range selected',
    viewsAriaLabel: 'Operating analysis views',
    signInAgain: 'Sign in again to load operating analysis.',
    loading: 'Loading operating analysis...',
    refreshing: 'Refreshing operating analysis...',
    loadingHint: 'You can change the date range or open Live Overview while this runs.',
    ready: 'Operating analysis is ready.',
    partialError: 'Some operating analysis refresh jobs failed.',
    failed: 'Failed to load operating analysis.',
    refreshTimeout: 'Refresh did not complete before timeout.',
    tabs: {
        all_vehicles: 'Fleet summary',
        per_vehicle: 'Per vehicle per date',
        per_vehicle_summary: 'Per vehicle summary',
        driving_behavior: 'Driving behavior',
    },
    table: {
        columnsVisible: '{visible} of {total} columns visible',
        rowsMatch: '{filtered} of {total} rows match',
        resultCount: '{filtered} of {total} rows shown',
        search: 'Search rows',
        searchPlaceholder: 'Search all row data',
        clearSearch: 'Clear search',
        rows: 'Rows',
        exportXlsx: 'Export XLSX',
        exportPreparing: 'Preparing...',
        exportSuccess: 'Export ready. {rows} rows downloaded.',
        exportFailed: 'Export failed. Please try again.',
        columns: 'Columns',
        visibleColumns: 'Visible columns',
        shownColumns: 'Shown columns',
        hiddenColumns: 'Hidden columns',
        columnOrderHelp: 'Top to bottom matches table order.',
        moveColumnUp: 'Move {column} up',
        moveColumnDown: 'Move {column} down',
        closeColumns: 'Close columns',
        minimumColumnSelection: 'At least one column must remain visible.',
        reset: 'Reset',
        resetWidths: 'Reset widths',
        resizeColumn: 'Resize {column} column',
        resetColumnWidth: 'Reset {column} width',
        columnWidthHelp: 'Drag column edges to resize. Double-click a handle to reset one column.',
        sortBy: 'Sort by',
        defaultSort: 'Default order',
        sortAscending: 'Ascending',
        sortDescending: 'Descending',
        sortColumnAscending: 'Sort {column} ascending',
        sortColumnDescending: 'Sort {column} descending',
        clearSortColumn: 'Clear sort for {column}',
        expandRowDetails: 'Expand details for {row}',
        collapseRowDetails: 'Collapse details for {row}',
        rowDetailsTitle: 'Hidden row details',
        noHiddenDetails: 'No hidden columns for this row.',
        previous: 'Previous',
        next: 'Next',
        pageOf: 'Page {page} of {totalPages}',
        filteredBy: 'Filtered by',
        noColumns: 'No columns returned for this analysis.',
        noRows: 'No rows returned for this range.',
        noRowsMatch: 'No rows match this search.',
        resultPreparing: '{analysis} is still being prepared. The table will appear here automatically when it is ready.',
        resultUnavailable: '{analysis} is not available for this date range yet.',
        analysisNoRowsForVehicle: 'Vehicle found, but no operating-analysis rows exist for this date range.',
    },
    modal: {
        contentLabel: 'Operating analysis date range',
        close: 'Close',
        datePresets: 'Date presets',
        days: '{count} days',
        startDate: 'Start date',
        endDate: 'End date',
        cancel: 'Cancel',
        apply: 'Apply',
    },
    presets: {
        last7Days: 'Last 7 days',
        last30Days: 'Last 30 days',
        thisMonth: 'This month',
        previousMonth: 'Previous month',
    },
    validation: {
        selectBoth: 'Select both start and end dates.',
        invalidDate: 'Invalid date. Use YYYY-MM-DD.',
        startBeforeEnd: 'Start date must be on or before end date.',
        maxRange: 'Date range cannot exceed {max} days.',
        futureDate: 'Date range cannot be in the future.',
        afterLatestAvailable: 'Data is available through {date}. Use the Live Overview tab for today\'s live data.',
    },
    availability: {
        dataThrough: 'Data is available through {date}.',
        todayLivePreview: 'Use the Live Overview tab for today\'s live data.',
    },
    filters: {
        vehicleModels: 'Vehicle models',
        contentLabel: 'Filter operating analysis by vehicle model',
        allModels: 'All models',
        selectedCount: '{count} selected',
        vehicleCount: '{count} vehicles',
        minimumSelection: 'Select at least one vehicle model.',
        apply: 'Apply',
        cancel: 'Cancel',
        close: 'Close',
    },
    timing: {
        etaCalculating: 'Calculating finish time...',
        etaQueued: 'Refresh queued; waiting for worker.',
        etaExpectedBy: 'Expected by {time}',
        etaRemainingApprox: 'about {duration} remaining',
        etaRemaining: 'ETA {duration}',
        elapsed: 'Elapsed {duration}',
        seconds: '{count}s',
        minutes: '{count}m',
        hours: '{count}h',
    },
    statuses: {
        unknown: 'Unknown',
    },
    impact: {
        title: 'EV savings impact',
        actualSavings: 'Actual savings',
        evDistance: 'EV distance driven',
        co2Saved: 'CO2 saved',
        treeEquivalent: 'Tree-equivalent impact',
        viewOpportunity: 'View opportunity savings',
        opportunity: 'Opportunity',
        maxOperatingSavings: 'Max operating fleet savings',
        additionalOpportunity: 'Additional opportunity',
        operatingVehiclesBasis: 'Operating vehicles basis',
        opportunityCaptured: '{percent}% captured',
        opportunityRemaining: '{percent}% remaining',
        opportunityProgressTitle: 'Savings progress',
        opportunityTarget: 'Target: {value}',
        opportunityActualValue: '{value} actual',
        opportunityRemainingValue: '{value} remaining opportunity',
        notAvailable: 'Not available',
        opportunityUnavailable: 'Max operating fleet data is not available for this period.',
    },
};

const mergeOperatingAnalysisCopy = (copy = {}) => ({
    ...DEFAULT_OPERATING_ANALYSIS_COPY,
    ...copy,
    tabs: {
        ...DEFAULT_OPERATING_ANALYSIS_COPY.tabs,
        ...(copy.tabs || {}),
    },
    summary: copy.summary || {},
    table: {
        ...DEFAULT_OPERATING_ANALYSIS_COPY.table,
        ...(copy.table || {}),
    },
    modal: {
        ...DEFAULT_OPERATING_ANALYSIS_COPY.modal,
        ...(copy.modal || {}),
    },
    presets: {
        ...DEFAULT_OPERATING_ANALYSIS_COPY.presets,
        ...(copy.presets || {}),
    },
    validation: {
        ...DEFAULT_OPERATING_ANALYSIS_COPY.validation,
        ...(copy.validation || {}),
    },
    availability: {
        ...DEFAULT_OPERATING_ANALYSIS_COPY.availability,
        ...(copy.availability || {}),
    },
    filters: {
        ...DEFAULT_OPERATING_ANALYSIS_COPY.filters,
        ...(copy.filters || {}),
    },
    timing: {
        ...DEFAULT_OPERATING_ANALYSIS_COPY.timing,
        ...(copy.timing || {}),
    },
    statuses: {
        ...DEFAULT_OPERATING_ANALYSIS_COPY.statuses,
        ...(copy.statuses || {}),
    },
    impact: {
        ...DEFAULT_OPERATING_ANALYSIS_COPY.impact,
        ...(copy.impact || {}),
    },
    booleans: copy.booleans || {},
    units: copy.units || {},
    columnLabels: copy.columnLabels || {},
});

const formatTemplate = (template, values = {}) => (
    String(template || '').replace(/\{(\w+)\}/g, (match, key) => (
        values[key] !== undefined ? values[key] : match
    ))
);

const formatLocaleNumber = (value, locale) => Number(value).toLocaleString(locale || undefined);

const getTranslatedAnalysisLabel = (analysisType, copy) => (
    copy.tabs?.[analysisType] || getAnalysisLabel(analysisType)
);

const getStatusMessage = (analysisState, copy) => {
    if (analysisState.messageKey && copy[analysisState.messageKey]) {
        return copy[analysisState.messageKey];
    }
    return analysisState.message || '';
};

const sanitizeUserVisibleMessage = (message = '') => (
    String(message || '').replace(INTERNAL_SOURCE_PREFIX_PATTERN, '').trim()
);

const pad = (value) => String(value).padStart(2, '0');

const formatLocalDate = (date) => {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const parseLocalDate = (value) => {
    if (!value || !DATE_INPUT_PATTERN.test(value)) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    const date = new Date(year, month - 1, day);
    if (
        date.getFullYear() !== year
        || date.getMonth() !== month - 1
        || date.getDate() !== day
    ) {
        return null;
    }
    return date;
};

const addDays = (date, days) => {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate;
};

const daysInclusive = (dateFrom, dateTo) => {
    const startDate = parseLocalDate(dateFrom);
    const endDate = parseLocalDate(dateTo);
    if (!startDate || !endDate) return 0;
    const startTime = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endTime = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    return Math.floor((endTime - startTime) / 86400000) + 1;
};

const getLatestAvailableDate = () => addDays(new Date(), -1);

const getLatestAvailableInputValue = () => formatLocalDate(getLatestAvailableDate());

const getRangeKey = (range) => (
    range ? `${range.dateFrom || ''}:${range.dateTo || ''}` : ''
);

const normalizeVehicleModels = (vehicleModels = []) => (
    [...new Set((vehicleModels || []).map((value) => String(value || '').trim()).filter(Boolean))]
);

const getAnalysisRequestKey = (range, vehicleModels = []) => (
    `${getRangeKey(range)}:${JSON.stringify(normalizeVehicleModels(vehicleModels))}`
);

const getVehicleModelFilterFromResults = (results = {}) => (
    Object.values(results).find((result) => result?.vehicleModelFilter)?.vehicleModelFilter || null
);

const getPresetRanges = (copy = DEFAULT_OPERATING_ANALYSIS_COPY) => {
    const latestAvailableDate = getLatestAvailableDate();
    const startOfThisMonth = new Date(latestAvailableDate.getFullYear(), latestAvailableDate.getMonth(), 1);
    const startOfPreviousMonth = new Date(latestAvailableDate.getFullYear(), latestAvailableDate.getMonth() - 1, 1);
    const endOfPreviousMonth = addDays(startOfThisMonth, -1);

    return [
        {
            key: 'last7Days',
            label: copy.presets?.last7Days || DEFAULT_OPERATING_ANALYSIS_COPY.presets.last7Days,
            dateFrom: formatLocalDate(addDays(latestAvailableDate, -6)),
            dateTo: formatLocalDate(latestAvailableDate),
        },
        {
            key: 'last30Days',
            label: copy.presets?.last30Days || DEFAULT_OPERATING_ANALYSIS_COPY.presets.last30Days,
            dateFrom: formatLocalDate(addDays(latestAvailableDate, -29)),
            dateTo: formatLocalDate(latestAvailableDate),
        },
        {
            key: 'thisMonth',
            label: copy.presets?.thisMonth || DEFAULT_OPERATING_ANALYSIS_COPY.presets.thisMonth,
            dateFrom: formatLocalDate(startOfThisMonth),
            dateTo: formatLocalDate(latestAvailableDate),
        },
        {
            key: 'previousMonth',
            label: copy.presets?.previousMonth || DEFAULT_OPERATING_ANALYSIS_COPY.presets.previousMonth,
            dateFrom: formatLocalDate(startOfPreviousMonth),
            dateTo: formatLocalDate(endOfPreviousMonth),
        },
    ];
};

const formatDisplayDate = (value, locale) => {
    const date = parseLocalDate(value);
    if (!date) return '--';
    return date.toLocaleDateString(locale || undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const validateDateRange = ({ dateFrom, dateTo }, copy = DEFAULT_OPERATING_ANALYSIS_COPY, locale) => {
    const validationCopy = copy.validation || DEFAULT_OPERATING_ANALYSIS_COPY.validation;
    if (!dateFrom || !dateTo) return validationCopy.selectBoth || DEFAULT_OPERATING_ANALYSIS_COPY.validation.selectBoth;
    const startDate = parseLocalDate(dateFrom);
    const endDate = parseLocalDate(dateTo);
    if (!startDate || !endDate) return validationCopy.invalidDate || DEFAULT_OPERATING_ANALYSIS_COPY.validation.invalidDate;
    if (startDate > endDate) return validationCopy.startBeforeEnd || DEFAULT_OPERATING_ANALYSIS_COPY.validation.startBeforeEnd;
    const latestAvailableInputValue = getLatestAvailableInputValue();
    const latestAvailableDate = parseLocalDate(latestAvailableInputValue);
    if (latestAvailableDate && (startDate > latestAvailableDate || endDate > latestAvailableDate)) {
        return formatTemplate(
            validationCopy.afterLatestAvailable
                || validationCopy.futureDate
                || DEFAULT_OPERATING_ANALYSIS_COPY.validation.afterLatestAvailable,
            {
                date: formatDisplayDate(latestAvailableInputValue, locale),
            }
        );
    }
    if (daysInclusive(dateFrom, dateTo) > MAX_RANGE_DAYS) {
        return formatTemplate(validationCopy.maxRange || DEFAULT_OPERATING_ANALYSIS_COPY.validation.maxRange, {
            max: formatLocaleNumber(MAX_RANGE_DAYS, locale),
        });
    }
    return '';
};

const delay = (ms) => new Promise((resolve) => {
    window.setTimeout(resolve, ms);
});

const dedupeAnalysisTypes = (analysisTypes) => (
    [...new Set((analysisTypes || []).filter(Boolean))]
);

const activeJobKey = (job) => job.jobId || `${job.analysisType}:${job.cacheStatus}`;

const mergeRefreshJobs = (existingJobs = [], updatedJobs = []) => {
    const jobsByKey = new Map();
    [...existingJobs, ...updatedJobs].filter(Boolean).forEach((job) => {
        jobsByKey.set(activeJobKey(job), {
            ...jobsByKey.get(activeJobKey(job)),
            ...job,
        });
    });
    return [...jobsByKey.values()];
};

const resultToRefreshJob = (result) => ({
    jobId: result.jobId,
    cacheStatus: result.cacheStatus,
    analysisType: result.analysisType,
    statementName: result.statementName,
    scope: result.scope,
    snapshot: result.snapshot,
    timing: result.timing,
    error: result.error || '',
    errorCode: result.errorCode || '',
});

const createFailedRefreshJob = (analysisType, error) => ({
    jobId: `failed:${analysisType}`,
    cacheStatus: 'failed',
    analysisType,
    error: error?.message || 'Refresh request failed.',
    errorCode: error?.status ? String(error.status) : 'refresh_request_failed',
    timing: error?.data?.timing,
});

const getRefreshDeadlineMs = (jobs = []) => {
    const timeoutSeconds = jobs.reduce((maximum, job) => {
        const jobTimeout = Number(job?.timing?.timeoutSeconds);
        return Number.isFinite(jobTimeout) && jobTimeout > maximum ? jobTimeout : maximum;
    }, DEFAULT_REFRESH_TIMEOUT_SECONDS);
    return (timeoutSeconds * 1000) + REFRESH_TIMEOUT_BUFFER_MS;
};

const formatDuration = (seconds, copy = DEFAULT_OPERATING_ANALYSIS_COPY, locale) => {
    const value = Number(seconds);
    if (!Number.isFinite(value) || value < 0) return '';

    const roundedSeconds = Math.max(0, Math.ceil(value));
    const timingCopy = copy.timing || DEFAULT_OPERATING_ANALYSIS_COPY.timing;
    if (roundedSeconds < 60) {
        return formatTemplate(timingCopy.seconds, {
            count: formatLocaleNumber(roundedSeconds, locale),
        });
    }
    if (roundedSeconds < 3600) {
        return formatTemplate(timingCopy.minutes, {
            count: formatLocaleNumber(Math.ceil(roundedSeconds / 60), locale),
        });
    }
    return formatTemplate(timingCopy.hours, {
        count: formatLocaleNumber(Math.ceil(roundedSeconds / 3600), locale),
    });
};

const formatEstimatedCompletionTime = (remainingSeconds, locale, now = Date.now()) => {
    const value = Number(remainingSeconds);
    if (!Number.isFinite(value) || value < 0) return '';

    const currentDate = new Date(now);
    const completionDate = new Date(currentDate.getTime() + (value * 1000));
    const isSameLocalDate = currentDate.getFullYear() === completionDate.getFullYear()
        && currentDate.getMonth() === completionDate.getMonth()
        && currentDate.getDate() === completionDate.getDate();
    const options = isSameLocalDate
        ? { hour: 'numeric', minute: '2-digit' }
        : { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };

    return new Intl.DateTimeFormat(locale || 'en-US', options).format(completionDate);
};

const getStatusTimingMessage = (jobs = [], copy = DEFAULT_OPERATING_ANALYSIS_COPY, locale) => {
    const activeJobs = jobs.filter((job) => isRefreshActive(job.cacheStatus));
    if (activeJobs.length === 0) return '';

    const timingCopy = copy.timing || DEFAULT_OPERATING_ANALYSIS_COPY.timing;
    const remainingValues = activeJobs.map((job) => job?.timing?.estimatedSecondsRemaining);
    if (remainingValues.some((value) => value === null || value === undefined || Number.isNaN(Number(value)))) {
        const estimateSources = activeJobs.map((job) => job?.timing?.estimateSource);
        if (estimateSources.some((source) => source === 'queued')) {
            return timingCopy.etaQueued || DEFAULT_OPERATING_ANALYSIS_COPY.timing.etaQueued;
        }
        return timingCopy.etaCalculating || DEFAULT_OPERATING_ANALYSIS_COPY.timing.etaCalculating;
    }

    const remainingSeconds = Math.max(...remainingValues.map(Number));
    const completionTime = formatEstimatedCompletionTime(remainingSeconds, locale);
    const parts = [
        formatTemplate(timingCopy.etaExpectedBy || DEFAULT_OPERATING_ANALYSIS_COPY.timing.etaExpectedBy, {
            time: completionTime,
        }),
        formatTemplate(timingCopy.etaRemainingApprox || DEFAULT_OPERATING_ANALYSIS_COPY.timing.etaRemainingApprox, {
            duration: formatDuration(remainingSeconds, copy, locale),
        }),
    ];
    return parts.filter(Boolean).join(' · ');
};

const getColumnStorage = () => {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
};

const getIsMobileOperatingAnalysis = () => (
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(MOBILE_OPERATING_ANALYSIS_QUERY).matches
);

const useIsMobileOperatingAnalysis = () => {
    const [isMobile, setIsMobile] = useState(getIsMobileOperatingAnalysis);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return undefined;
        }

        const mediaQuery = window.matchMedia(MOBILE_OPERATING_ANALYSIS_QUERY);
        const handleChange = () => setIsMobile(mediaQuery.matches);

        handleChange();

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        }

        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
    }, []);

    return isMobile;
};

const AnalysisStatus = ({ status, message, jobs = [], showCacheDetails, copy, locale, mode = 'panel' }) => {
    const resolvedCopy = copy || DEFAULT_OPERATING_ANALYSIS_COPY;
    if (status === 'idle') return null;

    const isBusy = status === 'loading' || status === 'refreshing';
    const timingMessage = getStatusTimingMessage(jobs, resolvedCopy, locale);
    if (isBusy) {
        const detail = timingMessage
            || resolvedCopy.timing?.etaCalculating
            || DEFAULT_OPERATING_ANALYSIS_COPY.timing.etaCalculating;
        return (
            <div className="operating-analysis-loading-state">
                <LoadingScreen
                    mode={mode}
                    title={message}
                    message={resolvedCopy.loadingHint}
                    detail={detail}
                />
                {showCacheDetails && jobs.length > 0 && (
                    <span className="operating-analysis-job-list" aria-label={message}>
                        {jobs.map((job) => (
                            <span key={activeJobKey(job)}>
                                {getTranslatedAnalysisLabel(job.analysisType, resolvedCopy)}: {resolvedCopy.statuses?.[job.cacheStatus] || job.cacheStatus}
                            </span>
                        ))}
                    </span>
                )}
            </div>
        );
    }

    return (
        <div className={`operating-analysis-status ${status}`} role="status" aria-live="polite">
            <span>{message}</span>
            {timingMessage && (
                <span className="operating-analysis-status-timing">{timingMessage}</span>
            )}
            {showCacheDetails && jobs.length > 0 && (
                <span className="operating-analysis-job-list">
                    {jobs.map((job) => (
                        <span key={activeJobKey(job)}>
                            {getTranslatedAnalysisLabel(job.analysisType, resolvedCopy)}: {resolvedCopy.statuses?.[job.cacheStatus] || job.cacheStatus}
                        </span>
                    ))}
                </span>
            )}
        </div>
    );
};

const AnalysisSummary = ({ result, showCacheDetails, copy, locale }) => {
    if (!result) return null;
    const stats = getAnalysisSummaryStats(result, { showCacheDetails, copy: copy || DEFAULT_OPERATING_ANALYSIS_COPY, locale });

    return (
        <div className="operating-analysis-summary">
            {stats.map((stat) => (
                <div className="operating-analysis-stat" key={stat.label}>
                    <span>{stat.label}</span>
                    <strong>{stat.value}</strong>
                </div>
            ))}
        </div>
    );
};

const AnalysisMissingResult = ({ analysisType, isBusy, copy }) => {
    const resolvedCopy = copy || DEFAULT_OPERATING_ANALYSIS_COPY;
    const analysisLabel = getTranslatedAnalysisLabel(analysisType, resolvedCopy);
    const messageTemplate = isBusy
        ? resolvedCopy.table.resultPreparing
        : resolvedCopy.table.resultUnavailable;

    return (
        <div className="operating-analysis-empty" role="status">
            {formatTemplate(messageTemplate, { analysis: analysisLabel })}
        </div>
    );
};

const IMPACT_CARD_ICONS = {
    actualSavings: 'fa-money-bill-wave',
    evDistance: 'fa-route',
    co2Saved: 'fa-cloud',
    treeEquivalent: 'fa-seedling',
};

const getFiniteOpportunityValue = (detail) => {
    const value = Number(detail?.value);
    return Number.isFinite(value) ? value : null;
};

const getOpportunityBridgeStats = (opportunity) => {
    if (!opportunity?.isCalculable) return null;

    const actualSavings = getFiniteOpportunityValue(opportunity.actualSavings);
    const maxOperatingSavings = getFiniteOpportunityValue(opportunity.maxOperatingSavings);
    const additionalOpportunity = getFiniteOpportunityValue(opportunity.additionalOpportunity);
    if (
        actualSavings === null
        || maxOperatingSavings === null
        || additionalOpportunity === null
        || actualSavings < 0
        || maxOperatingSavings <= 0
        || additionalOpportunity < 0
    ) {
        return null;
    }

    const totalSavings = Math.max(maxOperatingSavings, actualSavings + additionalOpportunity);
    if (!Number.isFinite(totalSavings) || totalSavings <= 0) return null;

    const actualWidth = Math.min(100, Math.max(0, (actualSavings / totalSavings) * 100));
    const opportunityWidth = Math.min(100 - actualWidth, Math.max(0, (additionalOpportunity / totalSavings) * 100));
    return {
        actualWidth,
        opportunityWidth,
        capturedPercent: Math.round(actualWidth),
        remainingPercent: Math.round(opportunityWidth),
    };
};

const formatOpportunityPercentLabel = (template, percent, locale) => (
    formatTemplate(template, {
        percent: formatLocaleNumber(percent, locale),
    })
);

const OpportunityBridge = ({ opportunity, copy, locale }) => {
    const resolvedCopy = copy || DEFAULT_OPERATING_ANALYSIS_COPY;
    const actualSavings = opportunity?.actualSavings;
    const maxOperatingSavings = opportunity?.maxOperatingSavings;
    const additionalOpportunity = opportunity?.additionalOpportunity;
    const operatingVehiclesBasis = opportunity?.operatingVehiclesBasis;
    const bridgeStats = getOpportunityBridgeStats(opportunity);
    const fallbackDetails = [
        actualSavings,
        maxOperatingSavings,
        additionalOpportunity,
        operatingVehiclesBasis,
    ].filter(Boolean);

    if (!bridgeStats) {
        return (
            <div className="operating-analysis-opportunity-panel">
                <div className="operating-analysis-opportunity-fallback-grid">
                    {fallbackDetails.map((detail) => (
                        <div className="operating-analysis-opportunity-detail" key={detail.label}>
                            <span>{detail.label}</span>
                            <strong>{detail.formattedValue}</strong>
                        </div>
                    ))}
                </div>
                {!opportunity?.isCalculable && opportunity?.unavailableMessage && (
                    <p className="operating-analysis-opportunity-message">
                        {opportunity.unavailableMessage}
                    </p>
                )}
            </div>
        );
    }

    const capturedLabel = formatOpportunityPercentLabel(
        resolvedCopy.impact.opportunityCaptured,
        bridgeStats.capturedPercent,
        locale
    );
    const remainingLabel = formatOpportunityPercentLabel(
        resolvedCopy.impact.opportunityRemaining,
        bridgeStats.remainingPercent,
        locale
    );
    const targetLabel = formatTemplate(resolvedCopy.impact.opportunityTarget, {
        value: maxOperatingSavings.formattedValue,
    });
    const actualValueLabel = formatTemplate(resolvedCopy.impact.opportunityActualValue, {
        value: actualSavings.formattedValue,
    });
    const remainingValueLabel = formatTemplate(resolvedCopy.impact.opportunityRemainingValue, {
        value: additionalOpportunity.formattedValue,
    });
    const progressTitle = resolvedCopy.impact.opportunityProgressTitle;
    const progressValueText = `${capturedLabel}. ${remainingLabel}`;

    return (
        <div className="operating-analysis-opportunity-panel">
            <div
                className="operating-analysis-opportunity-bridge"
                role="group"
                aria-label={[
                    actualSavings?.label && `${actualSavings.label}: ${actualSavings.formattedValue}`,
                    additionalOpportunity?.label && `${additionalOpportunity.label}: ${additionalOpportunity.formattedValue}`,
                    maxOperatingSavings?.label && `${maxOperatingSavings.label}: ${maxOperatingSavings.formattedValue}`,
                ].filter(Boolean).join('. ')}
            >
                <div className="operating-analysis-opportunity-progress-head">
                    <span>{progressTitle}</span>
                    <strong>{targetLabel}</strong>
                </div>
                <div
                    className="operating-analysis-opportunity-track"
                    role="progressbar"
                    aria-label={progressTitle}
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-valuenow={bridgeStats.capturedPercent}
                    aria-valuetext={progressValueText}
                    style={{ '--opportunity-progress': `${bridgeStats.actualWidth}%` }}
                >
                    <div className="operating-analysis-opportunity-bar" aria-hidden="true">
                        <span
                            className="operating-analysis-opportunity-segment actual"
                            style={{ flexBasis: `${bridgeStats.actualWidth}%` }}
                        ></span>
                        <span
                            className="operating-analysis-opportunity-segment additional"
                            style={{ flexBasis: `${bridgeStats.opportunityWidth}%` }}
                        ></span>
                    </div>
                    <span className="operating-analysis-opportunity-current-marker" aria-hidden="true">
                        <span>{capturedLabel}</span>
                    </span>
                    <span className="operating-analysis-opportunity-target-marker" aria-hidden="true"></span>
                </div>
                <div className="operating-analysis-opportunity-value-row">
                    <span>{actualValueLabel}</span>
                    <span>{remainingValueLabel}</span>
                </div>
                <div className="operating-analysis-opportunity-scale">
                    <span>{remainingLabel}</span>
                </div>
                <div className="operating-analysis-opportunity-footer">
                    {operatingVehiclesBasis && (
                        <div className="operating-analysis-opportunity-context">
                            <span>{operatingVehiclesBasis.label}</span>
                            <strong>{operatingVehiclesBasis.formattedValue}</strong>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const AnalysisImpactStrip = ({ impactSummary, copy, locale, headerActions = null }) => {
    const resolvedCopy = copy || DEFAULT_OPERATING_ANALYSIS_COPY;
    const [isOpportunityOpen, setIsOpportunityOpen] = useState(false);
    const cards = impactSummary?.cards || [];
    const opportunity = impactSummary?.opportunity || null;
    const opportunityValue = opportunity?.isCalculable && opportunity?.additionalOpportunity?.summaryFormattedValue
        ? opportunity.additionalOpportunity.summaryFormattedValue
        : resolvedCopy.impact.notAvailable;
    if (cards.length === 0) return null;

    return (
        <section
            className="operating-analysis-impact"
            aria-labelledby="operating-analysis-impact-title"
        >
            <div className="operating-analysis-impact-header">
                <h3 id="operating-analysis-impact-title">
                    {resolvedCopy.impact.title}
                </h3>
                {(headerActions || opportunity) ? (
                    <div className="operating-analysis-impact-actions">
                        {headerActions}
                        {opportunity ? (
                            <button
                                type="button"
                                className="operating-analysis-opportunity-toggle"
                                onClick={() => setIsOpportunityOpen((isOpen) => !isOpen)}
                                aria-expanded={isOpportunityOpen}
                            >
                                <i className="fas fa-chart-line" aria-hidden="true"></i>
                                <span>{resolvedCopy.impact.viewOpportunity}</span>
                                <strong>
                                    {resolvedCopy.impact.opportunity}: {opportunityValue}
                                </strong>
                                <i
                                    className={`fas ${isOpportunityOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}
                                    aria-hidden="true"
                                ></i>
                            </button>
                        ) : null}
                    </div>
                ) : null}
            </div>

            {cards.length > 0 && (
                <div className="operating-analysis-impact-cards">
                    {cards.map((card) => (
                        <article className="operating-analysis-impact-card" key={card.key}>
                            <span className="operating-analysis-impact-icon" aria-hidden="true">
                                <i className={`fas ${IMPACT_CARD_ICONS[card.key] || 'fa-chart-simple'}`}></i>
                            </span>
                            <span>{card.label}</span>
                            <strong title={card.detailFormattedValue || card.formattedValue}>
                                {card.formattedValue}
                            </strong>
                        </article>
                    ))}
                </div>
            )}

            {isOpportunityOpen && opportunity && (
                <OpportunityBridge
                    opportunity={opportunity}
                    copy={resolvedCopy}
                    locale={locale}
                />
            )}
        </section>
    );
};

const ANALYSIS_VIEW_ICONS = {
    all_vehicles: 'fa-chart-pie',
    per_vehicle: 'fa-car-side',
    per_vehicle_summary: 'fa-car',
    driving_behavior: 'fa-tachometer-alt',
};

const VEHICLE_LEVEL_ANALYSIS_TYPES = new Set(['per_vehicle', 'per_vehicle_summary', 'driving_behavior']);

const AnalysisViewTabs = ({ activeView, onChange, copy }) => (
    <div className="operating-analysis-tabs" role="tablist" aria-label={(copy || DEFAULT_OPERATING_ANALYSIS_COPY).viewsAriaLabel}>
        {CLIENT_OPERATING_ANALYSIS_TYPES.map((analysisType) => (
            <button
                key={analysisType}
                type="button"
                className={activeView === analysisType ? 'active' : ''}
                onClick={() => onChange(analysisType)}
                role="tab"
                aria-selected={activeView === analysisType}
            >
                <i className={`fas ${ANALYSIS_VIEW_ICONS[analysisType] || 'fa-table'}`} aria-hidden="true"></i>
                <span>{getTranslatedAnalysisLabel(analysisType, copy || DEFAULT_OPERATING_ANALYSIS_COPY)}</span>
            </button>
        ))}
    </div>
);

const getVehicleFilterLabel = (vehicleFilter = {}) => {
    if (!vehicleFilter) return '';
    const name = vehicleFilter.vehicle_label
        || vehicleFilter.vehicle_name
        || vehicleFilter.car_name
        || [vehicleFilter.make, vehicleFilter.model].filter(Boolean).join(' ').trim();
    const identifier = vehicleFilter.chassis_number || vehicleFilter.license_plate || '';
    return [name, identifier].filter(Boolean).join(' / ');
};

const getTableColumnClassName = (column, value) => (
    [getColumnLayoutClass(column), getColumnAlignmentClass(column, value)].filter(Boolean).join(' ')
);

const NO_TABLE_SORT = { column: '', direction: '' };

const getNextSortState = (sortState, column) => {
    if (!column) return NO_TABLE_SORT;
    if (sortState?.column !== column) {
        return { column, direction: SORT_DIRECTIONS.ASC };
    }
    if (sortState.direction === SORT_DIRECTIONS.ASC) {
        return { column, direction: SORT_DIRECTIONS.DESC };
    }
    return NO_TABLE_SORT;
};

const getSortAriaValue = (sortState, column) => {
    if (sortState?.column !== column) return undefined;
    if (sortState.direction === SORT_DIRECTIONS.ASC) return 'ascending';
    if (sortState.direction === SORT_DIRECTIONS.DESC) return 'descending';
    return undefined;
};

const getSortActionTitle = (sortState, column, columnLabel, copy) => {
    const nextSortState = getNextSortState(sortState, column);
    if (nextSortState.direction === SORT_DIRECTIONS.ASC) {
        return formatTemplate(copy.table.sortColumnAscending, { column: columnLabel });
    }
    if (nextSortState.direction === SORT_DIRECTIONS.DESC) {
        return formatTemplate(copy.table.sortColumnDescending, { column: columnLabel });
    }
    return formatTemplate(copy.table.clearSortColumn, { column: columnLabel });
};

const getSortIconClassName = (sortState, column) => {
    if (sortState?.column !== column) return 'fa-sort';
    return sortState.direction === SORT_DIRECTIONS.ASC ? 'fa-sort-up' : 'fa-sort-down';
};

const COLUMN_RESIZE_KEYBOARD_STEP = 16;
const COLUMN_RESIZE_KEYBOARD_FAST_STEP = 48;

const ColumnResizeHandle = ({
    column,
    columnLabel,
    columnWidth,
    active,
    copy,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onKeyDown,
    onDoubleClick,
}) => {
    const resizeLabel = formatTemplate(copy.table.resizeColumn, { column: columnLabel });
    const resetLabel = formatTemplate(copy.table.resetColumnWidth, { column: columnLabel });

    return (
        <span
            role="separator"
            aria-orientation="vertical"
            aria-label={resizeLabel}
            aria-valuemin={COLUMN_WIDTH_LIMITS.MIN}
            aria-valuemax={COLUMN_WIDTH_LIMITS.MAX}
            aria-valuenow={columnWidth}
            className={`operating-analysis-column-resize-handle${active ? ' is-active' : ''}`}
            tabIndex={0}
            title={`${resizeLabel}. ${resetLabel}`}
            onPointerDown={(event) => onPointerDown(event, column)}
            onPointerMove={(event) => onPointerMove(event, column)}
            onPointerUp={(event) => onPointerUp(event, column)}
            onPointerCancel={(event) => onPointerUp(event, column)}
            onKeyDown={(event) => onKeyDown(event, column)}
            onDoubleClick={(event) => onDoubleClick(event, column)}
            onClick={(event) => event.stopPropagation()}
        />
    );
};

const getRowDisplayLabel = (row, columns, copy, locale) => {
    const primaryColumn = columns[0];
    if (!primaryColumn) return copy.table.rowDetailsTitle;
    return formatCellValue(primaryColumn, row?.[primaryColumn], { copy, locale });
};

const getCellDisplayValue = (column, row, copy, locale) => (
    formatCellValue(column, row?.[column], { copy, locale })
);

const AnalysisRowDetails = ({ row, columns, copy, locale }) => {
    if (columns.length === 0) {
        return <p className="operating-analysis-row-details-empty">{copy.table.noHiddenDetails}</p>;
    }

    return (
        <dl className="operating-analysis-row-details-grid">
            {columns.map((column) => {
                const value = getCellDisplayValue(column, row, copy, locale);
                return (
                    <div className="operating-analysis-row-detail-item" key={column}>
                        <dt>{formatColumnLabel(column, copy.columnLabels)}</dt>
                        <dd className={getColumnAlignmentClass(column, row?.[column])} title={value}>
                            {value}
                        </dd>
                    </div>
                );
            })}
        </dl>
    );
};

const RowExpandButton = ({ expanded, rowLabel, detailId, onToggle, copy }) => {
    const labelTemplate = expanded ? copy.table.collapseRowDetails : copy.table.expandRowDetails;
    const label = formatTemplate(labelTemplate, { row: rowLabel });

    return (
        <button
            type="button"
            className={`operating-analysis-row-expand${expanded ? ' is-expanded' : ''}`}
            aria-expanded={expanded}
            aria-controls={detailId}
            aria-label={label}
            title={label}
            onClick={onToggle}
        >
            <i className="fas fa-chevron-right" aria-hidden="true"></i>
        </button>
    );
};

const AnalysisMobileCards = ({
    rowEntries,
    columns,
    hiddenColumns,
    analysisType,
    pagination,
    copy,
    locale,
    expandedRowKeys,
    onToggleRow,
}) => {
    const primaryColumn = columns[0];
    const detailColumns = columns.slice(1);
    const title = getTranslatedAnalysisLabel(analysisType, copy);

    return (
        <div className="operating-analysis-mobile-cards" role="list" aria-label={title}>
            {rowEntries.map(({ row, rowKey }, rowIndex) => {
                const displayRowKey = `${analysisType}-mobile-${pagination.startIndex + rowIndex}`;
                const primaryLabel = formatColumnLabel(primaryColumn, copy.columnLabels);
                const primaryValue = getCellDisplayValue(primaryColumn, row, copy, locale);
                const isExpanded = expandedRowKeys.has(rowKey);
                const detailId = `${rowKey}-mobile-details`;

                return (
                    <article className="operating-analysis-mobile-card" role="listitem" key={displayRowKey}>
                        <div className="operating-analysis-mobile-card-header">
                            <div className="operating-analysis-mobile-card-title">
                                <span>{primaryLabel}</span>
                                <strong title={primaryValue}>{primaryValue}</strong>
                            </div>
                            <RowExpandButton
                                expanded={isExpanded}
                                rowLabel={primaryValue}
                                detailId={detailId}
                                onToggle={() => onToggleRow(rowKey)}
                                copy={copy}
                            />
                        </div>

                        {detailColumns.length > 0 && (
                            <dl className="operating-analysis-mobile-card-fields">
                                {detailColumns.map((column) => (
                                    <div className="operating-analysis-mobile-card-field" key={column}>
                                        <dt>{formatColumnLabel(column, copy.columnLabels)}</dt>
                                        <dd
                                            className={getColumnAlignmentClass(column, row[column])}
                                            title={getCellDisplayValue(column, row, copy, locale)}
                                        >
                                            {getCellDisplayValue(column, row, copy, locale)}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        )}

                        {isExpanded && (
                            <div
                                className="operating-analysis-mobile-card-details"
                                id={detailId}
                                role="region"
                                aria-label={`${copy.table.rowDetailsTitle}: ${primaryValue}`}
                            >
                                <strong>{copy.table.rowDetailsTitle}</strong>
                                <AnalysisRowDetails
                                    row={row}
                                    columns={hiddenColumns}
                                    copy={copy}
                                    locale={locale}
                                />
                            </div>
                        )}
                    </article>
                );
            })}
        </div>
    );
};

const AnalysisTable = ({
    result,
    rangeKey,
    vehicleFilter = null,
    filterCopy = {},
    copy,
    locale,
    onExportXlsxClick = () => {},
}) => {
    const resolvedCopy = copy || DEFAULT_OPERATING_ANALYSIS_COPY;
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [isColumnPickerOpen, setIsColumnPickerOpen] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState([]);
    const [columnWidths, setColumnWidths] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [sortState, setSortState] = useState(NO_TABLE_SORT);
    const [expandedRowKeys, setExpandedRowKeys] = useState(() => new Set());
    const [resizingColumn, setResizingColumn] = useState('');
    const [exportState, setExportState] = useState({ status: 'idle', message: '' });
    const columnPickerRef = useRef(null);
    const columnPickerButtonRef = useRef(null);
    const columnResetKeyRef = useRef('');
    const activeResizeRef = useRef(null);
    const resizeFrameRef = useRef(null);
    const isMobileLayout = useIsMobileOperatingAnalysis();

    const analysisType = result?.analysisType || 'all_vehicles';
    const isRtl = locale === 'ar-EG';
    const columnPickerId = `operating-analysis-columns-${analysisType}`;
    const columnPickerTitleId = `operating-analysis-columns-title-${analysisType}`;
    const columnPickerHelpId = `operating-analysis-columns-help-${analysisType}`;
    const exportStatusId = `operating-analysis-export-status-${analysisType}`;
    const availableColumns = useMemo(() => result?.columns || [], [result]);
    const availableColumnsKey = useMemo(() => availableColumns.join('\u001f'), [availableColumns]);
    const exportColumnLabels = useMemo(() => (
        availableColumns.reduce((labels, column) => ({
            ...labels,
            [column]: formatColumnLabel(column, resolvedCopy.columnLabels),
        }), {})
    ), [availableColumns, resolvedCopy.columnLabels]);
    const rows = useMemo(() => result?.rows || [], [result]);
    const rowEntries = useMemo(() => rows.map((row, rowIndex) => ({
        row,
        rowIndex,
        rowKey: getAnalysisRowKey({ analysisType, row, rowIndex }),
    })), [analysisType, rows]);
    const vehicleFilteredEntries = useMemo(() => (
        rowEntries.filter(({ row }) => doesRowMatchVehicleIdentity(row, vehicleFilter, analysisType))
    ), [analysisType, rowEntries, vehicleFilter]);
    const searchedEntries = useMemo(() => (
        vehicleFilteredEntries.filter(({ row }) => doesRowMatchSearchQuery(row, searchQuery, {
            columns: availableColumns,
            copy: resolvedCopy,
            locale,
        }))
    ), [availableColumns, locale, resolvedCopy, searchQuery, vehicleFilteredEntries]);
    const sortedEntries = useMemo(() => (
        sortRowsByColumn(searchedEntries, sortState, {
            getRow: (entry) => entry.row,
            getStableIndex: (entry) => entry.rowIndex,
        })
    ), [searchedEntries, sortState]);
    const hasVehicleFilter = Boolean(vehicleFilter && Object.values(vehicleFilter).some(Boolean));
    const vehicleFilterLabel = getVehicleFilterLabel(vehicleFilter);

    useLayoutEffect(() => {
        const resetKey = `${analysisType}:${rangeKey}:${availableColumnsKey}`;
        const storedColumns = readStoredVisibleColumns(analysisType, availableColumns, getColumnStorage());
        const storedWidths = readStoredColumnWidths(analysisType, availableColumns, getColumnStorage());
        setVisibleColumns(storedColumns || getDefaultVisibleColumns(analysisType, availableColumns));
        setColumnWidths(resolveColumnWidths(availableColumns, storedWidths));
        setSearchQuery('');
        setSortState(NO_TABLE_SORT);
        setExpandedRowKeys(new Set());
        setResizingColumn('');
        setExportState({ status: 'idle', message: '' });
        setPage(1);
        setIsColumnPickerOpen((isOpen) => (
            columnResetKeyRef.current && columnResetKeyRef.current !== resetKey ? false : isOpen
        ));
        columnResetKeyRef.current = resetKey;
    }, [analysisType, availableColumns, availableColumnsKey, rangeKey]);

    useEffect(() => {
        setPage(1);
    }, [searchQuery, sortState.column, sortState.direction, vehicleFilter, rows]);

    const tableColumns = useMemo(() => (
        resolveVisibleColumns({
            analysisType,
            availableColumns,
            selectedColumns: visibleColumns,
        })
    ), [analysisType, availableColumns, visibleColumns]);
    const tableColumnSet = useMemo(() => new Set(tableColumns), [tableColumns]);
    const hiddenColumns = useMemo(() => (
        availableColumns.filter((column) => !tableColumnSet.has(column))
    ), [availableColumns, tableColumnSet]);
    useEffect(() => {
        if (sortState.column && !tableColumnSet.has(sortState.column)) {
            setSortState(NO_TABLE_SORT);
        }
    }, [sortState.column, tableColumnSet]);
    const desktopTableWidth = useMemo(() => (
        Math.max(
            tableColumns.reduce((width, column) => width + (columnWidths[column] || getColumnPreferredWidth(column)), 0),
            640,
        )
    ), [columnWidths, tableColumns]);
    const tableShellStyle = isMobileLayout
        ? undefined
        : { '--operating-analysis-table-width': `${desktopTableWidth}px` };
    const columnSelectionSummary = formatTemplate(resolvedCopy.table.columnsVisible, {
        visible: formatLocaleNumber(tableColumns.length, locale),
        total: formatLocaleNumber(availableColumns.length, locale),
    });
    const columnSelectionCount = `${formatLocaleNumber(tableColumns.length, locale)}/${formatLocaleNumber(availableColumns.length, locale)}`;
    const resultCountSummary = formatTemplate(resolvedCopy.table.resultCount, {
        filtered: formatLocaleNumber(searchedEntries.length, locale),
        total: formatLocaleNumber(vehicleFilteredEntries.length, locale),
    });
    const canExportRows = sortedEntries.length > 0 && tableColumns.length > 0;
    const isExporting = exportState.status === 'preparing';

    const closeColumnPicker = useCallback((restoreFocus = false) => {
        setIsColumnPickerOpen(false);
        if (restoreFocus) {
            columnPickerButtonRef.current?.focus();
        }
    }, []);

    useEffect(() => {
        if (!isColumnPickerOpen) return undefined;

        const closeOnOutsidePointer = (event) => {
            const pickerNode = columnPickerRef.current;
            if (pickerNode && !pickerNode.contains(event.target)) {
                closeColumnPicker();
            }
        };

        document.addEventListener('mousedown', closeOnOutsidePointer);
        document.addEventListener('touchstart', closeOnOutsidePointer);

        return () => {
            document.removeEventListener('mousedown', closeOnOutsidePointer);
            document.removeEventListener('touchstart', closeOnOutsidePointer);
        };
    }, [closeColumnPicker, isColumnPickerOpen]);

    const persistVisibleColumns = useCallback((nextColumns) => {
        const resolvedColumns = resolveVisibleColumns({
            analysisType,
            availableColumns,
            selectedColumns: nextColumns,
        });

        writeStoredVisibleColumns(analysisType, resolvedColumns, getColumnStorage());
        setPage(1);
        return resolvedColumns;
    }, [analysisType, availableColumns]);

    useEffect(() => {
        if (!isColumnPickerOpen) return undefined;

        const closeOnEscape = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeColumnPicker(true);
            }
        };

        document.addEventListener('keydown', closeOnEscape);

        return () => {
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [closeColumnPicker, isColumnPickerOpen]);

    useEffect(() => () => {
        if (resizeFrameRef.current) {
            cancelAnimationFrame(resizeFrameRef.current);
        }
        document.body.classList.remove('operating-analysis-column-is-resizing');
    }, []);

    const pagination = useMemo(() => (
        getPaginationRange({
            page,
            pageSize,
            total: sortedEntries.length,
        })
    ), [page, pageSize, sortedEntries.length]);

    const pagedEntries = useMemo(() => (
        paginateRows(sortedEntries, pagination.page, pagination.pageSize)
    ), [pagination.page, pagination.pageSize, sortedEntries]);

    const toggleColumn = (column) => {
        setVisibleColumns((previousColumns) => {
            const currentColumns = resolveVisibleColumns({
                analysisType,
                availableColumns,
                selectedColumns: previousColumns,
            });
            const isSelected = currentColumns.includes(column);
            if (isSelected && currentColumns.length <= 1) {
                return currentColumns;
            }
            const nextColumns = isSelected
                ? currentColumns.filter((selectedColumn) => selectedColumn !== column)
                : [...currentColumns, column];

            return persistVisibleColumns(nextColumns);
        });
    };

    const moveColumn = (column, direction) => {
        setVisibleColumns((previousColumns) => {
            const currentColumns = resolveVisibleColumns({
                analysisType,
                availableColumns,
                selectedColumns: previousColumns,
            });
            const currentIndex = currentColumns.indexOf(column);
            const nextIndex = currentIndex + direction;
            if (currentIndex === -1 || nextIndex < 0 || nextIndex >= currentColumns.length) {
                return currentColumns;
            }

            const nextColumns = [...currentColumns];
            [nextColumns[currentIndex], nextColumns[nextIndex]] = [nextColumns[nextIndex], nextColumns[currentIndex]];

            return persistVisibleColumns(nextColumns);
        });
    };

    const resetColumns = () => {
        const defaultColumns = getDefaultVisibleColumns(analysisType, availableColumns);
        clearStoredVisibleColumns(analysisType, getColumnStorage());
        setVisibleColumns(defaultColumns);
        setPage(1);
    };

    const updateColumnWidth = useCallback((column, width, { persist = false } = {}) => {
        const clampedWidth = clampColumnWidth(width) || getColumnPreferredWidth(column);

        setColumnWidths((currentWidths) => {
            const nextWidths = {
                ...resolveColumnWidths(availableColumns, currentWidths),
                [column]: clampedWidth,
            };

            if (persist) {
                writeStoredColumnWidths(analysisType, nextWidths, getColumnStorage());
            }

            return nextWidths;
        });

        return clampedWidth;
    }, [analysisType, availableColumns]);

    const resetColumnWidths = () => {
        clearStoredColumnWidths(analysisType, getColumnStorage());
        setColumnWidths(resolveColumnWidths(availableColumns, {}));
    };

    const resetSingleColumnWidth = useCallback((event, column) => {
        event.preventDefault();
        event.stopPropagation();
        updateColumnWidth(column, getColumnPreferredWidth(column), { persist: true });
    }, [updateColumnWidth]);

    const finishColumnResize = useCallback((event) => {
        const activeResize = activeResizeRef.current;
        if (!activeResize || (
            activeResize.pointerId !== undefined
            && event?.pointerId !== undefined
            && event.pointerId !== activeResize.pointerId
        )) return;

        event?.preventDefault();
        event?.stopPropagation();

        if (resizeFrameRef.current) {
            cancelAnimationFrame(resizeFrameRef.current);
            resizeFrameRef.current = null;
        }

        updateColumnWidth(activeResize.column, activeResize.nextWidth ?? activeResize.startWidth, { persist: true });

        try {
            event?.currentTarget?.releasePointerCapture?.(activeResize.pointerId);
        } catch {
            // Pointer capture can already be released by the browser.
        }

        activeResizeRef.current = null;
        setResizingColumn('');
        document.body.classList.remove('operating-analysis-column-is-resizing');
    }, [updateColumnWidth]);

    const scheduleResizeFrame = useCallback(() => {
        if (resizeFrameRef.current) return;

        resizeFrameRef.current = requestAnimationFrame(() => {
            resizeFrameRef.current = null;
            const activeResize = activeResizeRef.current;
            if (!activeResize) return;
            updateColumnWidth(activeResize.column, activeResize.nextWidth ?? activeResize.startWidth);
        });
    }, [updateColumnWidth]);

    const handleColumnResizePointerDown = useCallback((event, column) => {
        if (event.button !== undefined && event.button !== 0) return;

        event.preventDefault();
        event.stopPropagation();

        const startWidth = columnWidths[column] || getColumnPreferredWidth(column);
        activeResizeRef.current = {
            column,
            pointerId: event.pointerId,
            startX: event.clientX,
            startWidth,
            nextWidth: startWidth,
        };
        setResizingColumn(column);
        document.body.classList.add('operating-analysis-column-is-resizing');
        event.currentTarget?.setPointerCapture?.(event.pointerId);
    }, [columnWidths]);

    const handleColumnResizePointerMove = useCallback((event) => {
        const activeResize = activeResizeRef.current;
        if (!activeResize || (
            activeResize.pointerId !== undefined
            && event.pointerId !== undefined
            && event.pointerId !== activeResize.pointerId
        )) return;

        event.preventDefault();
        event.stopPropagation();

        const dragDelta = (event.clientX - activeResize.startX) * (isRtl ? -1 : 1);
        activeResize.nextWidth = clampColumnWidth(activeResize.startWidth + dragDelta) || getColumnPreferredWidth(activeResize.column);
        scheduleResizeFrame();
    }, [isRtl, scheduleResizeFrame]);

    const handleColumnResizeKeyDown = useCallback((event, column) => {
        const currentWidth = columnWidths[column] || getColumnPreferredWidth(column);
        const step = event.shiftKey ? COLUMN_RESIZE_KEYBOARD_FAST_STEP : COLUMN_RESIZE_KEYBOARD_STEP;
        let nextWidth = null;

        if (event.key === 'Home') {
            nextWidth = COLUMN_WIDTH_LIMITS.MIN;
        } else if (event.key === 'End') {
            nextWidth = COLUMN_WIDTH_LIMITS.MAX;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            const visualDelta = event.key === 'ArrowRight' ? step : -step;
            nextWidth = currentWidth + (isRtl ? visualDelta * -1 : visualDelta);
        } else {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        updateColumnWidth(column, nextWidth, { persist: true });
    }, [columnWidths, isRtl, updateColumnWidth]);

    const handlePageSizeChange = (event) => {
        setPageSize(Number(event.target.value));
        setPage(1);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setExpandedRowKeys(new Set());
    };

    const clearSearch = () => {
        setSearchQuery('');
        setExpandedRowKeys(new Set());
    };

    const toggleSortColumn = (column) => {
        setSortState((currentSortState) => getNextSortState(currentSortState, column));
        setExpandedRowKeys(new Set());
    };

    const handleMobileSortColumnChange = (event) => {
        const column = event.target.value;
        setSortState(column ? { column, direction: SORT_DIRECTIONS.ASC } : NO_TABLE_SORT);
        setExpandedRowKeys(new Set());
    };

    const toggleMobileSortDirection = () => {
        setSortState((currentSortState) => {
            if (!currentSortState.column) return currentSortState;
            return {
                ...currentSortState,
                direction: currentSortState.direction === SORT_DIRECTIONS.ASC
                    ? SORT_DIRECTIONS.DESC
                    : SORT_DIRECTIONS.ASC,
            };
        });
        setExpandedRowKeys(new Set());
    };

    const toggleRowExpansion = useCallback((rowKey) => {
        setExpandedRowKeys((currentKeys) => {
            const nextKeys = new Set(currentKeys);
            if (nextKeys.has(rowKey)) {
                nextKeys.delete(rowKey);
            } else {
                nextKeys.add(rowKey);
            }
            return nextKeys;
        });
    }, []);

    const exportTableToXlsx = async () => {
        if (!canExportRows || isExporting) return;

        const generatedAt = new Date();
        const exportRows = sortedEntries.map(({ row }) => row);
        setExportState({
            status: 'preparing',
            message: resolvedCopy.table.exportPreparing,
        });

        const filename = getSafeXlsxFilename([
            'operating-analysis',
            analysisType,
            rangeKey,
            formatLocalDate(generatedAt),
        ]);

        await Promise.resolve();

        onExportXlsxClick({
            analysisType,
            rowCount: exportRows.length,
            filteredRowCount: sortedEntries.length,
            visibleColumnCount: tableColumns.length,
        });

        try {
            const blob = await buildOperatingAnalysisXlsxBlob({
                analysisType,
                analysisLabel: getTranslatedAnalysisLabel(analysisType, resolvedCopy),
                rangeKey,
                rows: exportRows,
                visibleColumns: tableColumns,
                allColumns: availableColumns,
                columnLabels: exportColumnLabels,
                copy: resolvedCopy,
                locale,
                rightToLeft: isRtl,
                searchQuery,
                sortState,
                vehicleFilterLabel,
                generatedAt,
            });

            downloadBlob(blob, filename);
            setExportState({
                status: 'success',
                message: formatTemplate(resolvedCopy.table.exportSuccess, {
                    rows: formatLocaleNumber(exportRows.length, locale),
                }),
            });
        } catch {
            setExportState({
                status: 'error',
                message: resolvedCopy.table.exportFailed,
            });
        }
    };

    if (!result) return null;

    if (availableColumns.length === 0) {
        return (
            <div className="operating-analysis-empty">
                {resolvedCopy.table.noColumns}
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <div className="operating-analysis-empty">
                {resolvedCopy.table.noRows}
            </div>
        );
    }

    if (vehicleFilteredEntries.length === 0) {
        return (
            <div className="operating-analysis-empty">
                {hasVehicleFilter && VEHICLE_LEVEL_ANALYSIS_TYPES.has(analysisType)
                    ? resolvedCopy.table.analysisNoRowsForVehicle || filterCopy.analysisNoRowsForVehicle || DEFAULT_OPERATING_ANALYSIS_COPY.table.analysisNoRowsForVehicle
                    : resolvedCopy.table.noRowsMatch}
            </div>
        );
    }

    return (
        <div className="operating-analysis-table-shell" style={tableShellStyle}>
            <div className="operating-analysis-table-controls">
                <div className="operating-analysis-table-meta">
                    <strong>{getTranslatedAnalysisLabel(analysisType, resolvedCopy)}</strong>
                    <span>
                        {columnSelectionSummary}
                    </span>
                    <span aria-live="polite">
                        {resultCountSummary}
                    </span>
                    {hasVehicleFilter && VEHICLE_LEVEL_ANALYSIS_TYPES.has(analysisType) && (
                        <span>
                            {formatTemplate(resolvedCopy.table.rowsMatch, {
                                filtered: formatLocaleNumber(vehicleFilteredEntries.length, locale),
                                total: formatLocaleNumber(rows.length, locale),
                            })}
                        </span>
                    )}
                    {hasVehicleFilter && VEHICLE_LEVEL_ANALYSIS_TYPES.has(analysisType) && vehicleFilterLabel && (
                        <span className="operating-analysis-filter-pill">
                            {resolvedCopy.table.filteredBy || filterCopy.filteredBy || DEFAULT_OPERATING_ANALYSIS_COPY.table.filteredBy} {vehicleFilterLabel}
                        </span>
                    )}
                </div>

                <div className="operating-analysis-table-commandbar">
                    <div className="operating-analysis-table-search-wrap">
                        <label className="operating-analysis-table-search">
                            <span className="operating-analysis-visually-hidden">{resolvedCopy.table.search}</span>
                            <i className="fas fa-search" aria-hidden="true"></i>
                            <input
                                type="search"
                                value={searchQuery}
                                onChange={handleSearchChange}
                                placeholder={resolvedCopy.table.searchPlaceholder}
                                aria-label={resolvedCopy.table.search}
                            />
                        </label>
                        {searchQuery && (
                            <button
                                type="button"
                                className="operating-analysis-search-clear"
                                onClick={clearSearch}
                                aria-label={resolvedCopy.table.clearSearch}
                                title={resolvedCopy.table.clearSearch}
                            >
                                <i className="fas fa-xmark" aria-hidden="true"></i>
                            </button>
                        )}
                    </div>

                    {isMobileLayout && (
                        <div className="operating-analysis-mobile-sort">
                            <label>
                                <span>{resolvedCopy.table.sortBy}</span>
                                <select value={sortState.column} onChange={handleMobileSortColumnChange}>
                                    <option value="">{resolvedCopy.table.defaultSort}</option>
                                    {tableColumns.map((column) => (
                                        <option value={column} key={column}>
                                            {formatColumnLabel(column, resolvedCopy.columnLabels)}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <button
                                type="button"
                                className="operating-analysis-table-action"
                                onClick={toggleMobileSortDirection}
                                disabled={!sortState.column}
                                aria-label={sortState.direction === SORT_DIRECTIONS.ASC
                                    ? resolvedCopy.table.sortAscending
                                    : resolvedCopy.table.sortDescending}
                            >
                                <i
                                    className={`fas ${sortState.direction === SORT_DIRECTIONS.ASC ? 'fa-sort-up' : 'fa-sort-down'}`}
                                    aria-hidden="true"
                                ></i>
                                <span>
                                    {sortState.direction === SORT_DIRECTIONS.ASC
                                        ? resolvedCopy.table.sortAscending
                                        : resolvedCopy.table.sortDescending}
                                </span>
                            </button>
                        </div>
                    )}

                    <div className="operating-analysis-table-actions">
                        <label className="operating-analysis-page-size">
                            <span>{resolvedCopy.table.rows}</span>
                            <select value={pageSize} onChange={handlePageSizeChange}>
                                {PAGE_SIZE_OPTIONS.map((option) => (
                                    <option value={option} key={option}>{formatLocaleNumber(option, locale)}</option>
                                ))}
                            </select>
                        </label>

                        <button
                            type="button"
                            className="operating-analysis-table-action"
                            onClick={exportTableToXlsx}
                            disabled={!canExportRows || isExporting}
                            aria-describedby={exportState.message ? exportStatusId : undefined}
                        >
                            <i
                                className={`fas ${isExporting ? 'fa-spinner fa-spin' : 'fa-file-excel'}`}
                                aria-hidden="true"
                            ></i>
                            <span>{isExporting ? resolvedCopy.table.exportPreparing : resolvedCopy.table.exportXlsx}</span>
                        </button>

                        <div className="operating-analysis-column-picker" ref={columnPickerRef}>
                        <button
                            ref={columnPickerButtonRef}
                            type="button"
                            className={`operating-analysis-table-action${isColumnPickerOpen ? ' is-open' : ''}`}
                            onClick={() => setIsColumnPickerOpen((isOpen) => !isOpen)}
                            aria-haspopup="dialog"
                            aria-expanded={isColumnPickerOpen}
                            aria-controls={columnPickerId}
                        >
                            <i className="fas fa-table-columns" aria-hidden="true"></i>
                            <span>{resolvedCopy.table.columns}</span>
                            <span className="operating-analysis-column-count">{columnSelectionCount}</span>
                            <i className="fas fa-chevron-down operating-analysis-column-caret" aria-hidden="true"></i>
                        </button>

                        {isColumnPickerOpen && (
                            <div
                                className="operating-analysis-column-popover"
                                id={columnPickerId}
                                role="dialog"
                                aria-labelledby={columnPickerTitleId}
                                aria-describedby={columnPickerHelpId}
                            >
                                <div className="operating-analysis-column-popover-header">
                                    <div className="operating-analysis-column-popover-title">
                                        <strong id={columnPickerTitleId}>{resolvedCopy.table.visibleColumns}</strong>
                                        <span>{columnSelectionSummary}</span>
                                    </div>
                                    <div className="operating-analysis-column-popover-actions">
                                        <button
                                            type="button"
                                            className="operating-analysis-column-reset"
                                            onClick={resetColumns}
                                        >
                                            {resolvedCopy.table.reset}
                                        </button>
                                        <button
                                            type="button"
                                            className="operating-analysis-column-reset"
                                            onClick={resetColumnWidths}
                                        >
                                            {resolvedCopy.table.resetWidths}
                                        </button>
                                        <button
                                            type="button"
                                            className="operating-analysis-column-close"
                                            onClick={() => closeColumnPicker(true)}
                                            aria-label={resolvedCopy.table.closeColumns}
                                        >
                                            <i className="fas fa-xmark" aria-hidden="true"></i>
                                        </button>
                                    </div>
                                </div>
                                <p className="operating-analysis-visually-hidden" id={columnPickerHelpId}>
                                    {resolvedCopy.table.minimumColumnSelection}
                                </p>
                                <p className="operating-analysis-column-width-help">
                                    {resolvedCopy.table.columnWidthHelp}
                                </p>
                                <div className="operating-analysis-column-list" aria-describedby={columnPickerHelpId}>
                                    <div
                                        className="operating-analysis-column-section"
                                        role="group"
                                        aria-label={resolvedCopy.table.shownColumns}
                                    >
                                        <div className="operating-analysis-column-section-header">
                                            <strong>{resolvedCopy.table.shownColumns}</strong>
                                            <span>{resolvedCopy.table.columnOrderHelp}</span>
                                        </div>
                                        <div className="operating-analysis-column-items">
                                            {tableColumns.map((column, index) => {
                                                const columnLabel = formatColumnLabel(column, resolvedCopy.columnLabels);
                                                const isLocked = tableColumns.length === 1;
                                                const isFirst = index === 0;
                                                const isLast = index === tableColumns.length - 1;
                                                return (
                                                    <div
                                                        key={`shown-${column}`}
                                                        className={`operating-analysis-column-row${isLocked ? ' is-disabled' : ''}`}
                                                    >
                                                        <label className="operating-analysis-column-toggle">
                                                            <input
                                                                type="checkbox"
                                                                checked
                                                                disabled={isLocked}
                                                                aria-describedby={isLocked ? columnPickerHelpId : undefined}
                                                                onChange={() => toggleColumn(column)}
                                                            />
                                                            <span className="operating-analysis-column-label">{columnLabel}</span>
                                                        </label>
                                                        <div className="operating-analysis-column-move-controls">
                                                            <button
                                                                type="button"
                                                                className="operating-analysis-column-move"
                                                                aria-label={formatTemplate(resolvedCopy.table.moveColumnUp, { column: columnLabel })}
                                                                disabled={isFirst}
                                                                onClick={() => moveColumn(column, -1)}
                                                            >
                                                                <i className="fas fa-arrow-up" aria-hidden="true"></i>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="operating-analysis-column-move"
                                                                aria-label={formatTemplate(resolvedCopy.table.moveColumnDown, { column: columnLabel })}
                                                                disabled={isLast}
                                                                onClick={() => moveColumn(column, 1)}
                                                            >
                                                                <i className="fas fa-arrow-down" aria-hidden="true"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {hiddenColumns.length > 0 && (
                                        <div
                                            className="operating-analysis-column-section"
                                            role="group"
                                            aria-label={resolvedCopy.table.hiddenColumns}
                                        >
                                            <div className="operating-analysis-column-section-header">
                                                <strong>{resolvedCopy.table.hiddenColumns}</strong>
                                            </div>
                                            <div className="operating-analysis-column-items">
                                                {hiddenColumns.map((column) => {
                                                    const columnLabel = formatColumnLabel(column, resolvedCopy.columnLabels);
                                                    return (
                                                        <div className="operating-analysis-column-row is-hidden" key={`hidden-${column}`}>
                                                            <label className="operating-analysis-column-toggle">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={false}
                                                                    onChange={() => toggleColumn(column)}
                                                                />
                                                                <span className="operating-analysis-column-label">{columnLabel}</span>
                                                            </label>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        </div>
                    </div>

                    {exportState.message && (
                        <div
                            id={exportStatusId}
                            className={`operating-analysis-export-status ${exportState.status}`}
                            role={exportState.status === 'error' ? 'alert' : 'status'}
                            aria-live="polite"
                        >
                            {exportState.message}
                        </div>
                    )}
                </div>
            </div>

            {searchedEntries.length === 0 ? (
                <div className="operating-analysis-empty operating-analysis-table-empty">
                    {resolvedCopy.table.noRowsMatch}
                </div>
            ) : isMobileLayout ? (
                <AnalysisMobileCards
                    rowEntries={pagedEntries}
                    columns={tableColumns}
                    hiddenColumns={hiddenColumns}
                    analysisType={analysisType}
                    pagination={pagination}
                    copy={resolvedCopy}
                    locale={locale}
                    expandedRowKeys={expandedRowKeys}
                    onToggleRow={toggleRowExpansion}
                />
            ) : (
                <div className="operating-analysis-table-wrapper">
                    <table className="operating-analysis-table">
                        <colgroup>
                            {tableColumns.map((column) => (
                                <col
                                    key={column}
                                    className={getColumnLayoutClass(column)}
                                    style={{ width: `${columnWidths[column] || getColumnPreferredWidth(column)}px` }}
                                />
                            ))}
                        </colgroup>
                        <thead>
                            <tr>
                                {tableColumns.map((column, columnIndex) => {
                                    const columnLabel = formatColumnLabel(column, resolvedCopy.columnLabels);
                                    const sortAriaValue = getSortAriaValue(sortState, column);
                                    const columnWidth = columnWidths[column] || getColumnPreferredWidth(column);
                                    return (
                                        <th
                                            key={column}
                                            className={[
                                                getTableColumnClassName(column),
                                                columnIndex === 0 ? 'operating-analysis-sticky-column' : '',
                                                resizingColumn === column ? 'is-resizing' : '',
                                            ].filter(Boolean).join(' ')}
                                            aria-label={columnLabel}
                                            aria-sort={sortAriaValue}
                                            style={{ width: `${columnWidth}px` }}
                                        >
                                            <div className="operating-analysis-header-cell">
                                                <button
                                                    type="button"
                                                    className={`operating-analysis-sort-button${sortState.column === column ? ' is-active' : ''}`}
                                                    onClick={() => toggleSortColumn(column)}
                                                    title={getSortActionTitle(sortState, column, columnLabel, resolvedCopy)}
                                                >
                                                    <span>{columnLabel}</span>
                                                    <i className={`fas ${getSortIconClassName(sortState, column)}`} aria-hidden="true"></i>
                                                </button>
                                                <ColumnResizeHandle
                                                    column={column}
                                                    columnLabel={columnLabel}
                                                    columnWidth={columnWidth}
                                                    active={resizingColumn === column}
                                                    copy={resolvedCopy}
                                                    onPointerDown={handleColumnResizePointerDown}
                                                    onPointerMove={handleColumnResizePointerMove}
                                                    onPointerUp={finishColumnResize}
                                                    onKeyDown={handleColumnResizeKeyDown}
                                                    onDoubleClick={resetSingleColumnWidth}
                                                />
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {pagedEntries.map(({ row, rowKey }) => {
                                const isExpanded = expandedRowKeys.has(rowKey);
                                const rowLabel = getRowDisplayLabel(row, tableColumns, resolvedCopy, locale);
                                const detailId = `${rowKey}-details`;

                                return (
                                    <React.Fragment key={rowKey}>
                                        <tr className={isExpanded ? 'is-expanded' : ''}>
                                            {tableColumns.map((column, columnIndex) => {
                                                const columnLabel = formatColumnLabel(column, resolvedCopy.columnLabels);
                                                const cellValue = getCellDisplayValue(column, row, resolvedCopy, locale);
                                                const cellClassName = [
                                                    getTableColumnClassName(column, row[column]),
                                                    columnIndex === 0 ? 'operating-analysis-sticky-column operating-analysis-primary-cell' : '',
                                                ].filter(Boolean).join(' ');

                                                return (
                                                    <td
                                                        key={column}
                                                        className={cellClassName}
                                                        data-label={columnLabel}
                                                        title={cellValue}
                                                    >
                                                        {columnIndex === 0 ? (
                                                            <div className="operating-analysis-primary-cell-content">
                                                                <RowExpandButton
                                                                    expanded={isExpanded}
                                                                    rowLabel={rowLabel}
                                                                    detailId={detailId}
                                                                    onToggle={() => toggleRowExpansion(rowKey)}
                                                                    copy={resolvedCopy}
                                                                />
                                                                <span>{cellValue}</span>
                                                            </div>
                                                        ) : (
                                                            <span>{cellValue}</span>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                        {isExpanded && (
                                            <tr className="operating-analysis-detail-row">
                                                <td colSpan={tableColumns.length}>
                                                    <div
                                                        className="operating-analysis-row-details"
                                                        id={detailId}
                                                        role="region"
                                                        aria-label={`${resolvedCopy.table.rowDetailsTitle}: ${rowLabel}`}
                                                    >
                                                        <strong>{resolvedCopy.table.rowDetailsTitle}</strong>
                                                        <AnalysisRowDetails
                                                            row={row}
                                                            columns={hiddenColumns}
                                                            copy={resolvedCopy}
                                                            locale={locale}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="operating-analysis-table-footer">
                <span>{getPaginationSummary({ page: pagination.page, pageSize: pagination.pageSize, total: sortedEntries.length, copy: resolvedCopy, locale })}</span>
                <div className="operating-analysis-pagination">
                    <button
                        type="button"
                        onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
                        disabled={pagination.page === 1}
                    >
                        {resolvedCopy.table.previous}
                    </button>
                    <span>
                        {formatTemplate(resolvedCopy.table.pageOf, {
                            page: formatLocaleNumber(pagination.page, locale),
                            totalPages: formatLocaleNumber(pagination.totalPages, locale),
                        })}
                    </span>
                    <button
                        type="button"
                        onClick={() => setPage((currentPage) => Math.min(pagination.totalPages, currentPage + 1))}
                        disabled={pagination.page === pagination.totalPages}
                    >
                        {resolvedCopy.table.next}
                    </button>
                </div>
            </div>
        </div>
    );
};

const DateRangeFilter = ({
    onApply,
    draftRange,
    setDraftRange,
    validationError,
    setValidationError,
    copy,
    locale,
    isRtl,
    maxDate,
    isApplyDisabled,
}) => {
    const resolvedCopy = copy || DEFAULT_OPERATING_ANALYSIS_COPY;
    const presets = useMemo(() => getPresetRanges(resolvedCopy), [resolvedCopy]);
    const selectedPreset = presets.find((preset) => (
        preset.dateFrom === draftRange.dateFrom && preset.dateTo === draftRange.dateTo
    ));
    const draftRangeLabel = `${formatDisplayDate(draftRange.dateFrom, locale)} - ${formatDisplayDate(draftRange.dateTo, locale)}`;
    const inputMaxDate = maxDate || getLatestAvailableInputValue();
    const latestAvailableLabel = formatDisplayDate(inputMaxDate, locale);
    const startMaxDate = draftRange.dateTo && parseLocalDate(draftRange.dateTo) && draftRange.dateTo <= inputMaxDate
        ? draftRange.dateTo
        : inputMaxDate;
    const endMinDate = draftRange.dateFrom && parseLocalDate(draftRange.dateFrom)
        ? draftRange.dateFrom
        : undefined;

    const handlePresetClick = (preset) => {
        setDraftRange({ dateFrom: preset.dateFrom, dateTo: preset.dateTo });
        setValidationError('');
    };

    const handleDateChange = (event) => {
        const { name, value } = event.target;
        setDraftRange((previous) => ({ ...previous, [name]: value }));
        setValidationError('');
    };

    return (
        <div className="operating-analysis-date-filter" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="operating-analysis-preset-cluster">
                <div className="operating-analysis-presets" role="group" aria-label={resolvedCopy.modal.datePresets}>
                    {presets.map((preset) => {
                        const isActive = selectedPreset?.key === preset.key;
                        return (
                            <button
                                key={preset.key}
                                type="button"
                                className={isActive ? 'active' : ''}
                                aria-pressed={isActive}
                                onClick={() => handlePresetClick(preset)}
                            >
                                {preset.label}
                            </button>
                        );
                    })}
                </div>
                <div className="operating-analysis-draft-range" aria-live="polite">
                    {draftRangeLabel}
                </div>
            </div>

            <div className="operating-analysis-availability-note">
                <span className="operating-analysis-availability-icon" aria-hidden="true">
                    <i className="fas fa-clock"></i>
                </span>
                <span>
                    {formatTemplate(resolvedCopy.availability.dataThrough, {
                        date: latestAvailableLabel,
                    })}{' '}
                    {resolvedCopy.availability.todayLivePreview}
                </span>
            </div>

            <div className="operating-analysis-date-fields">
                <label>
                    <span>{resolvedCopy.modal.startDate}</span>
                    <input
                        type="date"
                        name="dateFrom"
                        value={draftRange.dateFrom}
                        max={startMaxDate}
                        onChange={handleDateChange}
                    />
                </label>
                <label>
                    <span>{resolvedCopy.modal.endDate}</span>
                    <input
                        type="date"
                        name="dateTo"
                        value={draftRange.dateTo}
                        min={endMinDate}
                        max={inputMaxDate}
                        onChange={handleDateChange}
                    />
                </label>
                <button
                    type="button"
                    className="operating-analysis-primary operating-analysis-apply-button"
                    onClick={onApply}
                    disabled={isApplyDisabled}
                    aria-busy={isApplyDisabled}
                >
                    {resolvedCopy.modal.apply}
                </button>
            </div>

            {validationError && (
                <div className="operating-analysis-validation" role="alert">
                    {validationError}
                </div>
            )}
        </div>
    );
};

export default function ClientOperatingAnalysis({
    analysisClient = '',
    vehicleFilter = null,
    filterCopy = {},
    showCacheDetails = true,
    autoLoadDefaultRange = false,
    initialPresetKey = 'last30Days',
    // Customer portal passes false until its profile-scoped analysis client is resolved.
    isClientScopeReady = true,
}) {
    const { webtheme } = useContext(themeContext);
    const { weblanguage, jsonfile } = useContext(languageContext);
    const { token, readCookie, ensureClientUsername } = useContext(AuthContext);
    const operatingCopy = useMemo(() => (
        mergeOperatingAnalysisCopy(jsonfile?.[weblanguage]?.operatingAnalysis)
    ), [jsonfile, weblanguage]);
    const locale = weblanguage === 'ar' ? 'ar-EG' : 'en-US';
    const isRtl = weblanguage === 'ar';
    const defaultRange = useMemo(() => {
        const presetRanges = getPresetRanges(operatingCopy);
        return presetRanges.find((preset) => preset.key === initialPresetKey) || presetRanges[1];
    }, [initialPresetKey, operatingCopy]);
    const [draftRange, setDraftRange] = useState({
        dateFrom: defaultRange.dateFrom,
        dateTo: defaultRange.dateTo,
    });
    const [selectedRange, setSelectedRange] = useState(null);
    const [validationError, setValidationError] = useState('');
    const [activeView, setActiveView] = useState('all_vehicles');
    const [analysisState, setAnalysisState] = useState({
        status: 'idle',
        message: '',
        messageKey: '',
        jobs: [],
        results: {},
    });
    const [appliedVehicleModels, setAppliedVehicleModels] = useState([]);
    const requestIdRef = useRef(0);
    const autoLoadRef = useRef(false);
    const autoLoadScopeKeyRef = useRef('');
    const inFlightRangeKeyRef = useRef('');
    const [inFlightRangeKey, setInFlightRangeKey] = useState('');
    const resolvedAnalysisClient = String(analysisClient || '').trim();

    const getAuthToken = useCallback(() => (
        token || (typeof readCookie === 'function' ? readCookie() : null) || ''
    ), [readCookie, token]);

    const trackAnalysisEvent = useCallback((eventName, properties = {}) => {
        const authToken = getAuthToken();
        if (!authToken) return;

        trackProductAnalyticsEvent(eventName, {
            token: authToken,
            page: getCurrentAnalyticsPage(),
            properties,
        });
    }, [getAuthToken]);

    const setCurrentInFlightRange = useCallback((rangeKey) => {
        inFlightRangeKeyRef.current = rangeKey;
        setInFlightRangeKey(rangeKey);
    }, []);

    const clearInFlightRange = useCallback((requestId, rangeKey) => {
        if (requestIdRef.current !== requestId || inFlightRangeKeyRef.current !== rangeKey) return;
        setCurrentInFlightRange('');
    }, [setCurrentInFlightRange]);

    const fetchAllAnalysis = useCallback(async ({ client, dateFrom, dateTo, vehicleModels, authToken }) => {
        const requests = CLIENT_OPERATING_ANALYSIS_TYPES.map((analysisType) => (
            getCachedClientOperatingAnalysis({
                analysisType,
                client,
                dateFrom,
                dateTo,
                vehicleModels,
                token: authToken,
            })
        ));
        return Promise.all(requests);
    }, []);

    const pollRefreshJobs = useCallback(async ({ jobs, authToken, requestId, existingResults }) => {
        let currentJobs = jobs;
        const startedPollingAt = Date.now();
        const deadlineMs = getRefreshDeadlineMs(currentJobs);

        while (Date.now() - startedPollingAt <= deadlineMs) {
            if (requestIdRef.current !== requestId) return currentJobs;

            const activeJobs = currentJobs.filter((job) => isRefreshActive(job.cacheStatus));
            if (activeJobs.length === 0) return currentJobs;

            await delay(POLL_INTERVAL_MS);
            if (requestIdRef.current !== requestId) return currentJobs;

            const updates = await Promise.all(activeJobs.map((job) => (
                getClientOperatingAnalysisRefreshStatus({
                    jobId: job.jobId,
                    token: authToken,
                })
            )));
            currentJobs = mergeRefreshJobs(currentJobs, updates);

            setAnalysisState({
                status: 'refreshing',
                message: '',
                messageKey: 'refreshing',
                jobs: currentJobs,
                results: existingResults,
            });
        }

        const timeoutError = new Error('Refresh did not complete before timeout.');
        timeoutError.messageKey = 'refreshTimeout';
        timeoutError.jobs = currentJobs;
        timeoutError.results = existingResults;
        throw timeoutError;
    }, []);

    const requestRefreshJobs = useCallback(async ({
        client,
        dateFrom,
        dateTo,
        analysisTypes,
        authToken,
    }) => {
        if (analysisTypes.length === 0) return [];

        try {
            const refreshResponse = await requestClientOperatingAnalysisRefresh({
                client,
                dateFrom,
                dateTo,
                analysisTypes,
                token: authToken,
            });
            return refreshResponse.jobs || [];
        } catch (bulkError) {
            if (analysisTypes.length === 1) {
                return [createFailedRefreshJob(analysisTypes[0], bulkError)];
            }

            const settledRefreshes = await Promise.all(analysisTypes.map(async (analysisType) => {
                try {
                    const refreshResponse = await requestClientOperatingAnalysisRefresh({
                        client,
                        dateFrom,
                        dateTo,
                        analysisTypes: [analysisType],
                        token: authToken,
                    });
                    return refreshResponse.jobs || [];
                } catch (error) {
                    return [createFailedRefreshJob(analysisType, error)];
                }
            }));

            return settledRefreshes.flat();
        }
    }, []);

    const runAnalysis = useCallback(async (range, {
        preserveResults = false,
        vehicleModels = [],
        fallbackResults = {},
    } = {}) => {
        if (!isClientScopeReady) return;

        const normalizedVehicleModels = normalizeVehicleModels(vehicleModels);
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        const requestKey = getAnalysisRequestKey(range, normalizedVehicleModels);
        setCurrentInFlightRange(requestKey);

        const authToken = getAuthToken();
        if (!authToken) {
            setAnalysisState({
                status: 'error',
                message: '',
                messageKey: 'signInAgain',
                jobs: [],
                results: {},
            });
            clearInFlightRange(requestId, requestKey);
            return;
        }

        setAnalysisState((currentState) => ({
            status: 'loading',
            message: '',
            messageKey: 'loading',
            jobs: [],
            results: preserveResults ? currentState.results : {},
        }));

        try {
            const client = resolvedAnalysisClient || await ensureClientUsername();
            const firstPass = await fetchAllAnalysis({
                client,
                dateFrom: range.dateFrom,
                dateTo: range.dateTo,
                vehicleModels: normalizedVehicleModels,
                authToken,
            });
            if (requestIdRef.current !== requestId) return;

            const readyResults = {};
            const pendingJobs = [];
            const refreshTypes = [];

            firstPass.forEach((result) => {
                if (result.cacheStatus === 'ready') {
                    readyResults[result.analysisType] = result;
                    if (result.isStale) {
                        refreshTypes.push(result.analysisType);
                    }
                } else if (isRefreshActive(result.cacheStatus) && result.jobId) {
                    pendingJobs.push(resultToRefreshJob(result));
                } else {
                    refreshTypes.push(result.analysisType);
                }
            });

            let jobs = pendingJobs;
            const refreshAnalysisTypes = dedupeAnalysisTypes(refreshTypes);
            if (refreshAnalysisTypes.length > 0) {
                trackAnalysisEvent(PRODUCT_ANALYTICS_EVENTS.ANALYSIS_REFRESH_REQUESTED, {
                    date_from: range.dateFrom,
                    date_to: range.dateTo,
                    analysis_types: refreshAnalysisTypes,
                    analysis_type_count: refreshAnalysisTypes.length,
                    selected_model_count: normalizedVehicleModels.length,
                });
                const refreshJobs = await requestRefreshJobs({
                    client,
                    dateFrom: range.dateFrom,
                    dateTo: range.dateTo,
                    analysisTypes: refreshAnalysisTypes,
                    authToken,
                });
                jobs = mergeRefreshJobs(jobs, refreshJobs);
            }

            jobs = jobs.filter((job) => job.jobId);
            const hasActiveJobs = jobs.some((job) => isRefreshActive(job.cacheStatus));
            if (hasActiveJobs) {
                const displayedResults = preserveResults ? fallbackResults : readyResults;
                setAnalysisState({
                    status: 'refreshing',
                    message: '',
                    messageKey: 'refreshing',
                    jobs,
                    results: displayedResults,
                });
                jobs = await pollRefreshJobs({
                    jobs,
                    authToken,
                    requestId,
                    existingResults: displayedResults,
                });
            }

            if (requestIdRef.current !== requestId) return;

            const canUseFirstPassResults = refreshAnalysisTypes.length === 0
                && jobs.length === 0
                && Object.keys(readyResults).length === CLIENT_OPERATING_ANALYSIS_TYPES.length;
            let finalResults = canUseFirstPassResults ? readyResults : {};
            let finalActiveJobs = [];
            let finalAttempt = canUseFirstPassResults ? 3 : 0;

            while (finalAttempt < 3) {
                finalAttempt += 1;
                const finalPass = await fetchAllAnalysis({
                    client,
                    dateFrom: range.dateFrom,
                    dateTo: range.dateTo,
                    vehicleModels: normalizedVehicleModels,
                    authToken,
                });
                if (requestIdRef.current !== requestId) return;

                const collectedResults = {};
                const collectedActiveJobs = [];
                finalPass.forEach((result) => {
                    if (result.cacheStatus === 'ready') {
                        collectedResults[result.analysisType] = result;
                    } else if (isRefreshActive(result.cacheStatus) && result.jobId) {
                        collectedActiveJobs.push(resultToRefreshJob(result));
                    }
                });
                finalResults = collectedResults;
                finalActiveJobs = collectedActiveJobs;

                jobs = mergeRefreshJobs(jobs, finalActiveJobs).filter((job) => job.jobId);
                if (finalActiveJobs.length === 0) break;

                const displayedResults = preserveResults ? fallbackResults : finalResults;
                setAnalysisState({
                    status: 'refreshing',
                    message: '',
                    messageKey: 'refreshing',
                    jobs,
                    results: displayedResults,
                });
                jobs = await pollRefreshJobs({
                    jobs,
                    authToken,
                    requestId,
                    existingResults: displayedResults,
                });
                finalActiveJobs = [];
            }

            const activeJobs = jobs.filter((job) => isRefreshActive(job.cacheStatus));
            if (activeJobs.length > 0) {
                setAnalysisState({
                    status: 'error',
                    message: '',
                    messageKey: 'refreshTimeout',
                    jobs,
                    results: preserveResults ? fallbackResults : finalResults,
                });
                clearInFlightRange(requestId, requestKey);
                return;
            }

            const failedJobs = jobs.filter((job) => ['failed', 'timed_out'].includes(job.cacheStatus));
            const hasFinalResults = Object.keys(finalResults).length > 0;
            const hasMissingResults = Object.keys(finalResults).length < CLIENT_OPERATING_ANALYSIS_TYPES.length;
            const messageKey = failedJobs.length > 0
                ? (hasFinalResults ? 'partialError' : 'failed')
                : (hasFinalResults ? (hasMissingResults ? 'partialError' : 'ready') : 'failed');
            const failureDetail = sanitizeUserVisibleMessage(
                failedJobs.map((job) => job.error).filter(Boolean)[0] || ''
            );

            const isSuccessful = messageKey === 'ready';
            setAnalysisState({
                status: isSuccessful ? 'ready' : 'error',
                message: failureDetail,
                messageKey: failureDetail ? '' : messageKey,
                jobs,
                results: isSuccessful || !preserveResults ? finalResults : fallbackResults,
            });
            if (isSuccessful) {
                const responseFilter = getVehicleModelFilterFromResults(finalResults);
                setAppliedVehicleModels(responseFilter?.selected || normalizedVehicleModels);
                const loadedAnalysisTypes = Object.keys(finalResults).sort();
                trackAnalysisEvent(PRODUCT_ANALYTICS_EVENTS.ANALYSIS_LOADED, {
                    date_from: range.dateFrom,
                    date_to: range.dateTo,
                    loaded_analysis_types: loadedAnalysisTypes,
                    row_counts: loadedAnalysisTypes.reduce((counts, analysisType) => ({
                        ...counts,
                        [analysisType]: finalResults[analysisType]?.rowCount ?? finalResults[analysisType]?.rows?.length ?? 0,
                    }), {}),
                    result_status: 'ready',
                    selected_model_count: normalizedVehicleModels.length,
                });
            }
            clearInFlightRange(requestId, requestKey);
        } catch (error) {
            if (requestIdRef.current !== requestId) return;
            setAnalysisState({
                status: 'error',
                message: error.messageKey ? '' : sanitizeUserVisibleMessage(error.message || ''),
                messageKey: error.messageKey || (error.message ? '' : 'failed'),
                jobs: error.jobs || [],
                results: preserveResults ? fallbackResults : (error.results || {}),
            });
            clearInFlightRange(requestId, requestKey);
        }
    }, [clearInFlightRange, ensureClientUsername, fetchAllAnalysis, getAuthToken, isClientScopeReady, pollRefreshJobs, requestRefreshJobs, resolvedAnalysisClient, setCurrentInFlightRange, trackAnalysisEvent]);

    const handleApply = () => {
        if (!isClientScopeReady) return;

        const error = validateDateRange(draftRange, operatingCopy, locale);
        if (error) {
            setValidationError(error);
            return;
        }

        const nextRange = { ...draftRange };
        const nextRangeKey = getRangeKey(nextRange);
        const isSameRange = Boolean(selectedRange && getRangeKey(selectedRange) === nextRangeKey);
        const nextVehicleModels = isSameRange ? appliedVehicleModels : [];
        if (inFlightRangeKeyRef.current === getAnalysisRequestKey(nextRange, nextVehicleModels)) return;

        setSelectedRange(nextRange);
        if (!isSameRange) {
            setAppliedVehicleModels([]);
        }
        setValidationError('');
        const matchingPreset = getPresetRanges(operatingCopy).find((preset) => (
            preset.dateFrom === nextRange.dateFrom && preset.dateTo === nextRange.dateTo
        ));
        trackAnalysisEvent(PRODUCT_ANALYTICS_EVENTS.ANALYSIS_DATE_RANGE_CHANGED, {
            date_from: nextRange.dateFrom,
            date_to: nextRange.dateTo,
            preset_key: matchingPreset?.key || 'custom',
            selected_model_count: nextVehicleModels.length,
        });
        runAnalysis(nextRange, {
            preserveResults: isSameRange,
            vehicleModels: nextVehicleModels,
            fallbackResults: analysisState.results,
        });
    };

    const handleVehicleModelsApply = (vehicleModels) => {
        if (!selectedRange || inFlightRangeKeyRef.current) return;

        const responseFilter = getVehicleModelFilterFromResults(analysisState.results);
        const allModelsSelected = vehicleModels.length === 0;
        const selectedModelSet = new Set(vehicleModels);
        const totalVehicleCount = Number(responseFilter?.totalVehicleCount || 0);
        const selectedVehicleCount = allModelsSelected
            ? totalVehicleCount
            : (responseFilter?.options || [])
                .filter((option) => selectedModelSet.has(option.value))
                .reduce((total, option) => total + Number(option.vehicleCount || 0), 0);
        trackAnalysisEvent(PRODUCT_ANALYTICS_EVENTS.ANALYSIS_VEHICLE_MODEL_FILTER_APPLIED, {
            date_from: selectedRange.dateFrom,
            date_to: selectedRange.dateTo,
            selected_model_count: allModelsSelected ? Number(responseFilter?.options?.length || 0) : vehicleModels.length,
            total_model_count: Number(responseFilter?.options?.length || 0),
            selected_vehicle_count: selectedVehicleCount,
            total_vehicle_count: totalVehicleCount,
            all_models_selected: allModelsSelected,
        });
        runAnalysis(selectedRange, {
            preserveResults: true,
            vehicleModels,
            fallbackResults: analysisState.results,
        });
    };

    const handleAnalysisViewChange = (nextView) => {
        if (nextView === activeView) return;

        trackAnalysisEvent(PRODUCT_ANALYTICS_EVENTS.ANALYSIS_VIEW_CHANGED, {
            from_view: activeView,
            to_view: nextView,
        });
        setActiveView(nextView);
    };

    useEffect(() => {
        const scopeKey = isClientScopeReady ? (resolvedAnalysisClient || 'fallback') : 'pending';
        if (autoLoadScopeKeyRef.current === scopeKey) return;
        autoLoadScopeKeyRef.current = scopeKey;
        autoLoadRef.current = false;
        setAppliedVehicleModels([]);
    }, [isClientScopeReady, resolvedAnalysisClient]);

    useEffect(() => {
        if (!autoLoadDefaultRange || !isClientScopeReady || autoLoadRef.current) return;

        autoLoadRef.current = true;
        const nextRange = {
            dateFrom: defaultRange.dateFrom,
            dateTo: defaultRange.dateTo,
        };
        setDraftRange(nextRange);
        setSelectedRange(nextRange);
        setAppliedVehicleModels([]);
        setValidationError('');
        runAnalysis(nextRange, { vehicleModels: [] });
    }, [autoLoadDefaultRange, defaultRange.dateFrom, defaultRange.dateTo, isClientScopeReady, runAnalysis]);

    const activeResult = analysisState.results[activeView];
    const hasResults = Object.keys(analysisState.results).length > 0;
    const vehicleModelFilter = useMemo(
        () => getVehicleModelFilterFromResults(analysisState.results),
        [analysisState.results]
    );
    const impactSummary = useMemo(() => (
        getOperatingImpactSummary({
            results: analysisState.results,
            copy: operatingCopy,
            locale,
        })
    ), [analysisState.results, locale, operatingCopy]);
    const selectedRangeKey = selectedRange ? getRangeKey(selectedRange) : 'none';
    const isDraftSelectedRange = Boolean(selectedRange && getRangeKey(selectedRange) === getRangeKey(draftRange));
    const draftRequestKey = getAnalysisRequestKey(
        draftRange,
        isDraftSelectedRange ? appliedVehicleModels : []
    );
    const latestAvailableInputValue = getLatestAvailableInputValue();
    const isApplyDisabled = !isClientScopeReady || inFlightRangeKey === draftRequestKey;
    const statusMessage = getStatusMessage(analysisState, operatingCopy);
    const shouldShowStatus = analysisState.status !== 'ready' || !hasResults;
    const isAnalysisBusy = analysisState.status === 'loading' || analysisState.status === 'refreshing';
    const shouldShowViewTabs = Boolean(selectedRange) && (hasResults || isAnalysisBusy);
    const shouldShowImpact = hasResults;
    const shouldShowVehicleModelFilter = Boolean(
        vehicleModelFilter?.isAvailable && vehicleModelFilter.options?.length > 1
    );
    const hasImpactCards = Boolean(impactSummary?.cards?.length);
    const vehicleModelFilterControl = shouldShowVehicleModelFilter ? (
        <VehicleModelFilter
            filter={vehicleModelFilter}
            appliedModels={appliedVehicleModels}
            onApply={handleVehicleModelsApply}
            isBusy={isAnalysisBusy}
            copy={operatingCopy.filters}
            locale={locale}
            isRtl={isRtl}
        />
    ) : null;

    return (
        <section className={`operating-analysis-section ${webtheme}-theme`} dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="operating-analysis-toolbar">
                <div className="operating-analysis-header-row">
                    <div className="operating-analysis-heading">
                        <h2>{operatingCopy.title}</h2>
                        <p>
                            {selectedRange
                                ? `${formatDisplayDate(selectedRange.dateFrom, locale)} - ${formatDisplayDate(selectedRange.dateTo, locale)}`
                                : operatingCopy.noRangeSelected}
                        </p>
                    </div>
                </div>
                <DateRangeFilter
                    onApply={handleApply}
                    draftRange={draftRange}
                    setDraftRange={setDraftRange}
                    validationError={validationError}
                    setValidationError={setValidationError}
                    copy={operatingCopy}
                    locale={locale}
                    isRtl={isRtl}
                    maxDate={latestAvailableInputValue}
                    isApplyDisabled={isApplyDisabled}
                />
            </div>

            {shouldShowStatus && (
                <AnalysisStatus
                    status={analysisState.status}
                    message={statusMessage}
                    jobs={analysisState.jobs}
                    showCacheDetails={showCacheDetails}
                    copy={operatingCopy}
                    locale={locale}
                    mode={hasResults ? 'inline' : 'panel'}
                />
            )}

            {shouldShowViewTabs && (
                <div className="operating-analysis-results" aria-busy={isAnalysisBusy}>
                    {vehicleModelFilterControl && !hasImpactCards ? (
                        <div className="operating-analysis-results-toolbar">
                            {vehicleModelFilterControl}
                        </div>
                    ) : null}
                    {shouldShowImpact && (
                        <AnalysisImpactStrip
                            impactSummary={impactSummary}
                            copy={operatingCopy}
                            locale={locale}
                            headerActions={hasImpactCards ? vehicleModelFilterControl : null}
                        />
                    )}
                    <div className="operating-analysis-view-switch">
                        <AnalysisViewTabs
                            activeView={activeView}
                            onChange={handleAnalysisViewChange}
                            copy={operatingCopy}
                        />
                    </div>
                    {activeResult ? (
                        <>
                            <AnalysisSummary
                                result={activeResult}
                                showCacheDetails={showCacheDetails}
                                copy={operatingCopy}
                                locale={locale}
                            />
                            <AnalysisTable
                                result={activeResult}
                                rangeKey={selectedRangeKey}
                                vehicleFilter={vehicleFilter}
                                filterCopy={filterCopy}
                                copy={operatingCopy}
                                locale={locale}
                                onExportXlsxClick={({ analysisType, rowCount, filteredRowCount, visibleColumnCount }) => (
                                    trackAnalysisEvent(PRODUCT_ANALYTICS_EVENTS.EXPORT_XLSX_CLICKED, {
                                        analysis_type: analysisType,
                                        export_type: 'xlsx',
                                        row_count: rowCount,
                                        filtered_row_count: filteredRowCount,
                                        visible_column_count: visibleColumnCount,
                                    })
                                )}
                            />
                        </>
                    ) : (
                        <AnalysisMissingResult
                            analysisType={activeView}
                            isBusy={isAnalysisBusy}
                            copy={operatingCopy}
                        />
                    )}
                </div>
            )}

        </section>
    );
}
